import { useCallback, useRef } from "react";

export const useAudioSynth = (muted) => {
  const ctxRef = useRef(null);

  const ctx = () => {
    if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    return ctxRef.current;
  };

  const tone = useCallback((freq, dur, type = "sine", delay = 0, endFreq, vol = 0.18) => {
    const c = ctx();
    const t = c.currentTime + delay;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (endFreq) o.frequency.exponentialRampToValueAtTime(endFreq, t + dur);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(g).connect(c.destination);
    o.start(t);
    o.stop(t + dur + 0.02);
  }, []);

  const noise = useCallback((dur) => {
    const c = ctx();
    const buf = c.createBuffer(1, c.sampleRate * dur, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const s = c.createBufferSource();
    const g = c.createGain();
    g.gain.value = 0.25;
    s.buffer = buf;
    s.connect(g).connect(c.destination);
    s.start();
  }, []);

  const sounds = {
    flip: () => tone(400, 0.08, "triangle", 0, 800, 0.1),
    correct: () => { tone(523, 0.1); tone(659, 0.1, "sine", 0.06); tone(784, 0.14, "sine", 0.12); },
    wrong: () => tone(220, 0.3, "sawtooth", 0, 110, 0.12),
    bomb: () => { noise(0.4); tone(120, 0.5, "sine", 0, 40, 0.35); },
    tick: () => tone(1200, 0.03, "square", 0, null, 0.05),
    win: () => [523, 659, 784, 988, 1047].forEach((f, i) => tone(f, 0.25, "triangle", i * 0.08)),
    gameover: () => [262, 196, 156].forEach((f, i) => tone(f, 0.4, "triangle", i * 0.22, null, 0.2)),
  };

  const soundsRef = useRef(sounds);
  soundsRef.current = sounds;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  return useCallback((name) => {
    if (mutedRef.current) return;
    try { soundsRef.current[name](); } catch (e) { /* audio unavailable */ }
  }, []);
};
